/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, Dictionary, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import * as React from 'react';

import Button from '../button/Button';
import { IFormKeyProps } from './form-builder.props';

/**
 * The state of the form builder.
 */
export interface IFormBuilderState {
    /**
     * The captured values.
     */
    values: string[];

    /**
     * If the form has been submitted.
     */
    isSubmitted: boolean;
}

/**
 * The properties of the form builder.
 */
export interface IFormBuilderProps {
    /**
     * The component showing confirmation after form submitted successfully.
     */
    confirmationComponent: React.ReactNode;

    /**
     * The heading for the component shown above the input boxes.
     */
    heading: string;

    /**
     * The text shown on the submit button.
     */
    submitButtonText: string;

    /**
     * The keys of the form values to capture, which will be shown as labels for the inputs.
     */
    keys: IFormKeyProps[];

    /**
     * The error message shown when submitting the form and required value is missing.
     */
    requiredValueMissingErrorMessage: string;

    /**
     * The action to perform when the form is submitted.
     * @param { formKey: IFormKeyProps, value: string[] } entry - The form data as key-value pairs to submit.
     */
    onSubmit(entry?: { formKey: IFormKeyProps; value: string }[]): Promise<void>;
}

/**
 * The interface for properties of form builder error.
 */
export interface IFormBuilderErrorProps {
    errorMessage?: string;
}

/**
 * The error component in form builder.
 * @param errorProperties - The error properties.
 * @param errorProperties.errorMessage - The error message to show.
 * @returns The error component.
 */
export const FormBuilderErrorComponent: React.FC<IFormBuilderErrorProps> = ({ errorMessage }) => {
    if (!errorMessage) {
        return null;
    }

    return (
        <span className="msc-alert msc-alert-noborder">
            <span>{errorMessage}</span>
        </span>
    );
};

/**
 * The component to capture form data.
 */
export class FormBuilder extends React.Component<IFormBuilderProps, IFormBuilderState> {
    private readonly requiredValueMissing: Dictionary<string, boolean>;

    public constructor(props: IFormBuilderProps) {
        super(props);
        this.state = { values: this.props.keys.map(() => ''), isSubmitted: false };

        // Setup a boolean dictionary to keep track if required value is missing for every field to submit.
        this.requiredValueMissing = new Dictionary<string, boolean>();
        for (const formKey of this.props.keys) {
            if (formKey.resourceId) {
                this.requiredValueMissing.setValue(formKey.resourceId, formKey.isRequired ?? false);
            }
        }
    }

    public shouldComponentUpdate(nextProps: IFormBuilderProps, nextState: IFormBuilderState): boolean {
        if (
            this.props.heading !== nextProps.heading ||
            this.props.confirmationComponent !== nextProps.confirmationComponent ||
            this.props.submitButtonText !== nextProps.submitButtonText ||
            this.props.requiredValueMissingErrorMessage !== nextProps.requiredValueMissingErrorMessage
        ) {
            return true;
        }

        // If any form key has changed, the component should update.
        if (
            !this.props.keys.every((formKey: IFormKeyProps, index: number) => {
                return (
                    nextProps.keys[Number(index)].isRequired === formKey.isRequired &&
                    nextProps.keys[Number(index)].resourceId === formKey.resourceId &&
                    nextProps.keys[Number(index)].resourceValue === formKey.resourceValue
                );
            })
        ) {
            return true;
        }

        // If any value has changed, the component should update.
        if (
            this.state.isSubmitted !== nextState.isSubmitted ||
            !this.state.values.every((value: string, index: number) => {
                return value === nextState[Number(index)];
            })
        ) {
            return true;
        }

        return false;
    }

    public render(): JSX.Element {
        return (
            <>
                {!this.state.isSubmitted && ArrayExtensions.hasElements(this.props.keys) && (
                    <div className="msc-form-builder__section col-sm-12 col-md-4 offset-md-4">
                        <h2 className="msc-form-builder__heading">{this.props.heading}</h2>
                        <div className="msc-form-builder__form-container">
                            <form
                                onSubmit={this._onSubmit}
                                className="msc-form-builder__form"
                                aria-label={this.props.submitButtonText}
                                autoComplete="on"
                            >
                                {this.props.keys
                                    .filter((formKey: IFormKeyProps) => {
                                        return (
                                            !ObjectExtensions.isNullOrUndefined(formKey.resourceId) &&
                                            !ObjectExtensions.isNullOrUndefined(formKey.resourceValue)
                                        );
                                    })
                                    .map((formKey: IFormKeyProps, index: number) => {
                                        const currentValue: string = this.state.values[Number(index)];
                                        return (
                                            <div key={formKey.resourceId} className="msc-form-builder__row">
                                                <label
                                                    id={`${formKey.resourceId!}-label`}
                                                    htmlFor={formKey.resourceId}
                                                    className="msc-form-builder__label"
                                                >
                                                    {`${formKey.resourceValue!}${formKey.isRequired ? ' *' : ''}`}
                                                    {this.requiredValueMissing.getValue(formKey.resourceId!) && (
                                                        <FormBuilderErrorComponent
                                                            errorMessage={this.props.requiredValueMissingErrorMessage}
                                                        />
                                                    )}
                                                    <input
                                                        type="text"
                                                        id={`${formKey.resourceId!}-input`}
                                                        className="msc-form-builder__input"
                                                        key={formKey.resourceId}
                                                        data-index={index}
                                                        onChange={this._onInputChange}
                                                        value={currentValue}
                                                        aria-label={formKey.resourceValue}
                                                        aria-required={formKey.isRequired ?? false}
                                                        required={formKey.isRequired ?? false}
                                                    />
                                                </label>
                                            </div>
                                        );
                                    })}
                                <div className="msc-form-builder__row">
                                    <Button
                                        aria-label={this.props.submitButtonText}
                                        className="btn msc-form-builder__btn-submit"
                                        disabled={this.isAnyRequiredValueMissing}
                                    >
                                        {this.props.submitButtonText}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                {this.state.isSubmitted && this.props.confirmationComponent}
            </>
        );
    }

    private get isAnyRequiredValueMissing(): boolean {
        const notFound: number = -1;
        return this.requiredValueMissing.getValues().findIndex((isMissing: boolean) => isMissing) !== notFound;
    }

    /**
     * The action to perform when the submit button is clicked.
     * @param event - The click event.
     * @returns The async result.
     */
    private readonly _onSubmit = async (event: React.SyntheticEvent): Promise<void> => {
        event.preventDefault(); // Prevents form submission
        const formData: { formKey: IFormKeyProps; value: string }[] = this.props.keys.map((formKey: IFormKeyProps, index: number) => {
            const currentValue: string = this.state.values[Number(index)];
            return {
                formKey: { resourceId: formKey.resourceId!, resourceValue: formKey.resourceValue },
                value: currentValue
            };
        });
        await this.props.onSubmit(formData);
        this.setState((previousState: Readonly<IFormBuilderState>) => {
            return { values: previousState.values, isSubmitted: true };
        });
    };

    /**
     * The action to perform when any input in the form has changed.
     * @param event - The input change event.
     * @returns The async result.
     */
    private readonly _onInputChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        event.preventDefault();
        const changedValueIndex: number = Number.parseInt(event.target.getAttribute('data-index') ?? '', 10);
        if (ObjectExtensions.isNullOrUndefined(changedValueIndex)) {
            return Promise.reject(new Error('form-builder: invalid value index.'));
        }
        const newState = {
            values: this.state.values.map((value: string, index: number) => {
                const currentKey: IFormKeyProps = this.props.keys[Number(index)];
                if (index === changedValueIndex && currentKey.resourceId) {
                    this.requiredValueMissing.setValue(
                        currentKey.resourceId,
                        (currentKey.isRequired ?? false) && event.target.value === ''
                    );
                    return event.target.value;
                }
                return value;
            })
        };
        this.setState(newState);
        return Promise.resolve();
    };
}
